import { useRef, useEffect } from "react";
import { REFERRAL_READY, COLLECT_READY, UNIVERSITY_READY } from './constants/featureFlags'
import toastUtil from './utils/toast'
import { prewarmNoKeyboardResize } from './utils/flutterStatusBar'
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { isApp } from "./utils/platform";
import CoachMark from "./components/CoachMark";
import {
  HOME_TUTORIAL_STEPS,
  markHomeTutorialSeen,
} from "./constants/homeTutorial";
import { useTutorialStore } from "./stores/tutorialStore";
import Splash from "./pages/Splash";
import Login from "./pages/Login";
import Welcome from "./pages/Welcome";
import PhoneSignup from "./pages/PhoneSignup";
import PhoneVerify from "./pages/PhoneVerify";
import Signup from "./pages/Signup";
import Register from "./pages/Register";
import ContactSync from "./pages/ContactSync";
import PermissionRequest from "./pages/PermissionRequest";
import ContactFriends from "./pages/ContactFriends";
import OnboardingImportRank from "./pages/OnboardingImportRank";
import OnboardingIntro from "./pages/OnboardingIntro";
import PlateListForm from "./pages/PlateListForm";
import MainHome from "./pages/MainHome";
import MainMapKakao from "./pages/MainMap";
import MapErrorBoundary from "./components/MapErrorBoundary";
import MainRanking from "./pages/MainRanking";
import Search from "./pages/Search";
import StoreReview from "./pages/StoreReview";
import PhotoEdit from "./pages/PhotoEdit";
import VisitDateAdd from "./pages/VisitDateAdd";
import PriceRangeAdd from "./pages/PriceRangeAdd";
import ReviewKeywordSelect from "./pages/ReviewKeywordSelect";
import TasteQuiz from "./pages/TasteQuiz";
import Support from "./pages/Support";
import SupportList from "./pages/SupportList";
import SupportThread from "./pages/SupportThread";
import ReviewKeywordSuggest from "./pages/ReviewKeywordSuggest";
import ReviewDetail from "./pages/ReviewDetail";
import ReviewComplete from "./pages/ReviewComplete";
import ReviewPhotoDetail from "./pages/ReviewPhotoDetail";
import NotificationList from "./pages/NotificationList";
import OtherUserProfile from "./pages/OtherUserProfile";
import TasteProfile from "./pages/TasteProfile";
import SavedPlaces from "./pages/SavedPlaces";
import SavedPlacesList from "./pages/SavedPlacesList";
import InstagramShare from "./pages/InstagramShare";
import SharedLink from "./pages/SharedLink";
import CollectGuide from "./pages/CollectGuide";
import Collect from "./pages/Collect";
import University from "./pages/University";
import UniversityEmail from "./pages/UniversityEmail";
import UniversityVerify from "./pages/UniversityVerify";
import UniversityDone from "./pages/UniversityDone";
import UniversityRejoin from "./pages/UniversityRejoin";
import UniversityGroup from "./pages/UniversityGroup";
import InviteFriends from "./pages/InviteFriends";
import Go from "./pages/Go";
import QrEvent from "./pages/QrEvent";
import GachaEvent from "./pages/event/gacha/GachaEvent";
import GachaVoucher from "./pages/event/gacha/GachaVoucher";
import MapLinkShare from "./pages/MapLinkShare";
import PlateCategory from "./pages/PlateCategory";
import PlateCategoryList from "./pages/PlateCategoryList";
import MainMy from "./pages/MainMy";
import ProfileEdit from "./pages/ProfileEdit";
import PasswordFind from "./pages/PasswordFind";
import FollowList from "./pages/FollowList";
import FollowRequests from "./pages/FollowRequests";
import Reviews from "./pages/Reviews";
import Settings from "./pages/Settings";
import AccountManagement from "./pages/AccountManagement";
import BlockedUsers from "./pages/BlockedUsers";
import NoticePage from "./pages/Notice";
import ConsentManagement from "./pages/ConsentManagement";
import AccountDeletion from "./pages/AccountDeletion";
import PrivacyPolicyPage from "./pages/legal/PrivacyPolicyPage";
import TermsPage from "./pages/legal/TermsPage";
import LocationTermsPage from "./pages/legal/LocationTermsPage";
import MarketingConsentPage from "./pages/legal/MarketingConsentPage";
import NaverCallback from "./pages/NaverCallback";
import PhoneVerificationTest from "./pages/PhoneVerificationTest";
import AdminLogin from "./admin/pages/AdminLogin";
import AdminStats from "./admin/pages/AdminStats";
import AdminUsers from "./admin/pages/AdminUsers";
import AdminPlaces from "./admin/pages/AdminPlaces";
import AdminReviews from "./admin/pages/AdminReviews";
import AdminCuratedFeed from "./admin/pages/AdminCuratedFeed";
import AdminReports from "./admin/pages/AdminReports";
import AdminInquiries from "./admin/pages/AdminInquiries";
import AdminNotices from "./admin/pages/AdminNotices";
import AdminPush from "./admin/pages/AdminPush";
import AdminReviewKeywords from "./admin/pages/AdminReviewKeywords";
import AdminReviewKeywordSuggestions from "./admin/pages/AdminReviewKeywordSuggestions";
import AdminPlaceSuggestions from "./admin/pages/AdminPlaceSuggestions";
import AdminMapSettings from "./admin/pages/AdminMapSettings";
import AdminPolicies from "./admin/pages/AdminPolicies";
import AdminToastPreview from "./admin/pages/AdminToastPreview";
import AdminInstagramTest from "./admin/pages/AdminInstagramTest";
import AdminPlaceNames from "./admin/pages/AdminPlaceNames";
import AdminAppVersions from "./admin/pages/AdminAppVersions";
import AdminPlaceCategories from "./admin/pages/AdminPlaceCategories";
import AdminDefaultReviewCategories from "./admin/pages/AdminDefaultReviewCategories";
import AdminRedirect from "./admin/pages/AdminRedirect";
import AdminTermsOfService from "./admin/pages/AdminTermsOfService";
import AdminPrivacyPolicy from "./admin/pages/AdminPrivacyPolicy";
import ProtectedRoute from "./components/ProtectedRoute";
import { useSwipeBack } from "./hooks/useSwipeBack";

/** 가게 ID가 바뀔 때 리마운트하여 로컬 비교 UI 상태가 꼬이지 않게 함 */
const StoreReviewRoute = () => {
  const { storeId } = useParams<{ storeId: string }>();
  return <StoreReview key={storeId ?? ""} />;
};

/**
 * 리뷰 ID가 바뀔 때 리마운트.
 *
 * ReviewDetail은 데이터를 useEffect(..., [])로 한 번만 읽고 review·comments 등
 * 리뷰별 로컬 상태를 들고 있다. 같은 라우트 안에서 id만 바뀌면(알림을 눌러 다른
 * 리뷰 상세로 바로 이동하는 경우) 컴포넌트가 리마운트되지 않아 URL만 바뀌고
 * 이전 리뷰의 본문·댓글이 그대로 남는다.
 */
const ReviewDetailRoute = () => {
  const { id } = useParams<{ id: string }>();
  return <ReviewDetail key={id ?? ""} />;
};

const AppRoutes = () => {
  // 스와이프 뒤로가기 시 이 컨테이너가 손가락을 따라 이동/스냅샷 캡처 대상이 됨
  const swipeContainerRef = useRef<HTMLDivElement>(null);
  useSwipeBack(swipeContainerRef);

  /*
   * 토스트 문구 미리보기 — **개발 중에만.**
   *
   *   ?toast=한 번에 최대 100장까지 가져와요
   *
   * 문구를 고칠 때마다 실제 조건(사진 100장 공유 등)을 재현하기 어려워서,
   * 주소만으로 띄워 보고 길이·줄바꿈을 눈으로 확인한다. 로그인이 필요한
   * 화면을 거치지 않아도 된다.
   *
   * 운영 빌드에서는 NODE_ENV 가 'production' 이라 이 블록이 통째로 사라진다.
   */
  const location0 = useLocation();

  /*
   * 화면을 옮길 때마다 '키보드로 웹뷰를 줄이지 마라'를 다시 못 박는다.
   *
   * 왜 — 실기기 측정(아이폰, 2026-10-30)
   * -----------------------------------
   * 자세히보기에서 답글을 쓸 때만 화면이 한 번 출렁였다. 재보니 웹뷰 높이가
   * 같이 움직였다 — 줄지 말아야 할 창이 줄었다는 뜻이다.
   *   답글   ih874 → 494 → 874,  sy 0 → 380 → 0
   *   댓글   ih874 → 874,        sy 0 → 0
   *
   * App.tsx 가 시작할 때 한 번 끄지만 그 값은 카운터로 관리된다. 중간에
   * 카운터가 0까지 내려가면 다시 켜지고, 한 번 켜지면 아무도 안 끈다.
   * 끄는 쪽은 같은 값을 다시 보내도 안전하므로(앱이 달라질 때만 setState)
   * 화면이 바뀔 때마다 한 번 더 보내 둔다. 카운터는 건드리지 않는다 —
   * prewarm 이 그런 용도로 이미 있던 함수다.
   */
  useEffect(() => {
    prewarmNoKeyboardResize();
  }, [location0.pathname]);

  const toastShownRef = useRef<string | null>(null);
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    const msg = new URLSearchParams(location0.search).get("toast");
    if (!msg || toastShownRef.current === msg) return;
    toastShownRef.current = msg;
    toastUtil.warning(msg, { duration: 8000 });
  }, [location0.search]);

  const tutorialActive = useTutorialStore((s) => s.active);
  const closeTutorial = useTutorialStore((s) => s.close);

  // 상시 immersive에서 콘텐츠를 노치 아래로 보정하는 클래스(index.css)를 라우트별로 토글.
  // 지도만 full-bleed(노치 뒤까지 지도)라 예외. 한 곳에서 결정해 이펙트 순서 이슈를 피한다.
  const location = useLocation();
  useEffect(() => {
    if (!isApp()) return;
    // 지도, 그리고 배경 영상이 있는 Welcome(/login)은 full-bleed(노치·홈인디케이터까지 덮음).
    const isFullBleed =
      location.pathname.startsWith("/main-map") ||
      location.pathname === "/login";
    document.body.classList.toggle("immersive-pad", !isFullBleed);
  }, [location.pathname]);
  return (
    <div
      ref={swipeContainerRef}
      className="app-swipe-root"
      style={{ width: "100%", height: "100%" }}
    >
      <Routes>
        {/* 스플래시 */}
        <Route path="/" element={<Splash />} />

        {/* 앱 첫 진입(가입 전) 3스텝 온보딩 — 최초 1회만(Splash가 분기) */}
        <Route path="/onboarding-intro" element={<OnboardingIntro />} />

        {/* 첫 진입(로그인/가입) */}
        <Route path="/login" element={<Welcome />} />
        {/* 소셜 로그인 선택 */}
        <Route path="/login/social" element={<Login />} />

        {/* 네이버 로그인 콜백 */}
        <Route path="/naver-callback" element={<NaverCallback />} />

        {/* 회원가입 - 휴대폰번호 입력 */}
        <Route path="/register/phone" element={<PhoneSignup />} />
        {/* 회원가입 - 문자 인증번호 입력 */}
        <Route path="/register/phone/verify" element={<PhoneVerify />} />
        {/* 회원가입 - 단계형(소셜 신규가입 등) */}
        <Route path="/signup" element={<Signup />} />
        {/* (구) 회원가입 - 폴백 */}
        <Route path="/register" element={<Register />} />

        {/* 신규 가입 권한 안내(연락처·알림 OS 권한 요청) */}
        <Route
          path="/permissions"
          element={
            <ProtectedRoute>
              <PermissionRequest />
            </ProtectedRoute>
          }
        />

        {/* 연락처 동기화 */}
        <Route
          path="/contact-sync"
          element={
            <ProtectedRoute>
              <ContactSync />
            </ProtectedRoute>
          }
        />

        {/* 연락처 친구 */}
        <Route
          path="/contact-friends"
          element={
            <ProtectedRoute>
              <ContactFriends />
            </ProtectedRoute>
          }
        />

        {/* 가입 온보딩: 지도 리스트 가져와 가본 곳 랭킹 */}
        <Route
          path="/onboarding-rank"
          element={
            <ProtectedRoute>
              <OnboardingImportRank />
            </ProtectedRoute>
          }
        />

        {/* 플레이트리스트 추가/수정 (풀페이지) */}
        <Route
          path="/platelist/new"
          element={
            <ProtectedRoute>
              <PlateListForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/platelist/:categoryId/edit"
          element={
            <ProtectedRoute>
              <PlateListForm />
            </ProtectedRoute>
          }
        />

        {/* 메인 - 홈 (피드) */}
        <Route
          path="/main-home"
          element={
            <ProtectedRoute>
              <MainHome />
            </ProtectedRoute>
          }
        />

        {/* 메인 - 랭킹 */}
        <Route
          path="/main-ranking"
          element={
            <ProtectedRoute>
              <MainRanking />
            </ProtectedRoute>
          }
        />

        {/* 메인 - 맵 (카카오 SDK 사용) */}
        <Route
          path="/main-map"
          element={
            <ProtectedRoute>
              <MapErrorBoundary>
                <MainMapKakao />
              </MapErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/*
          홈 화면 바로가기가 들어오는 자리. 앱에는 뜻(record)만 박혀 있고
          어느 화면인지는 Go 가 정한다 — 목적지를 바꿀 때 앱을 다시 안 올려도 된다.
          로그인 검사는 보내진 화면이 알아서 한다.
        */}
        <Route path="/go/:key" element={<Go />} />

        {/*
          * 매장 QR 이벤트 랜딩. QR로만 들어온다 — 메뉴나 다른 화면에서 링크하지 않는다.
          * 계정이 없는 사람이 먼저 보는 화면이라 ProtectedRoute 를 씌우지 않는다.
          */}
        <Route path="/qr" element={<QrEvent />} />

        {/* QR 캡슐 뽑기 이벤트 + 당첨 교환권. QR로만 들어온다(noindex). */}
        <Route path="/event/gacha" element={<GachaEvent />} />
        <Route path="/event/gacha/voucher" element={<GachaVoucher />} />

        {/* 검색 */}
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Search />
            </ProtectedRoute>
          }
        />

        {/* 맛집 리뷰 */}
        <Route
          path="/store-review/:storeId"
          element={
            <ProtectedRoute>
              <StoreReviewRoute />
            </ProtectedRoute>
          }
        />

        {/* 사진 편집 */}
        <Route
          path="/photo-edit/:storeId"
          element={
            <ProtectedRoute>
              <PhotoEdit />
            </ProtectedRoute>
          }
        />

        {/* 리뷰 상세 */}
        <Route
          path="/review-detail/:id"
          element={
            <ProtectedRoute>
              <ReviewDetailRoute />
            </ProtectedRoute>
          }
        />

        {/* 리뷰 사진 상세 */}
        <Route
          path="/review-photo/:reviewId/:imageIndex"
          element={
            <ProtectedRoute>
              <ReviewPhotoDetail />
            </ProtectedRoute>
          }
        />

        {/* 리뷰 완료 */}
        <Route
          path="/review-complete/:reviewId"
          element={
            <ProtectedRoute>
              <ReviewComplete />
            </ProtectedRoute>
          }
        />

        {/* 방문 일자 추가 */}
        <Route
          path="/visit-date-add"
          element={
            <ProtectedRoute>
              <VisitDateAdd />
            </ProtectedRoute>
          }
        />

        {/* 1인당 가격대 기록 */}
        <Route
          path="/price-range-add"
          element={
            <ProtectedRoute>
              <PriceRangeAdd />
            </ProtectedRoute>
          }
        />

        {/* 취향분석 (BETA) — 환경설정에서 들어온다 */}
        {/*
          * ProtectedRoute 를 씌우지 않는다 — 공유 링크를 받은 사람은 계정이
          * 없을 수 있고, 그 사람을 로그인 화면으로 보내면 막다른 길이다.
          * 화면 안에서 직접 갈라 download.html 로 보낸다.
          */}
        <Route path="/taste-quiz" element={<TasteQuiz />} />

        {/* 고객센터 문의. 목록(list)이 숫자 id 보다 먼저 와야 가로채이지 않는다. */}
        <Route path="/support" element={<Support />} />
        <Route path="/support/list" element={<SupportList />} />
        <Route path="/support/:id" element={<SupportThread />} />

        {/* 리뷰 키워드 선택 */}
        <Route
          path="/review-keyword-select"
          element={
            <ProtectedRoute>
              <ReviewKeywordSelect />
            </ProtectedRoute>
          }
        />

        {/* 키워드 제안 */}
        <Route
          path="/review-keyword-suggest"
          element={
            <ProtectedRoute>
              <ReviewKeywordSuggest />
            </ProtectedRoute>
          }
        />

        {/* 알림 리스트 */}
        <Route
          path="/notification-list"
          element={
            <ProtectedRoute>
              <NotificationList />
            </ProtectedRoute>
          }
        />

        {/* 다른 유저 프로필 */}
        <Route
          path="/user/:userId/profile"
          element={
            <ProtectedRoute>
              <OtherUserProfile />
            </ProtectedRoute>
          }
        />

        {/* 취향 유사도 분석 */}
        <Route
          path="/user/:userId/taste-profile"
          element={
            <ProtectedRoute>
              <TasteProfile />
            </ProtectedRoute>
          }
        />

        {/* 저장한 장소 */}
        <Route
          path="/user/:userId/saved-places"
          element={
            <ProtectedRoute>
              <SavedPlaces />
            </ProtectedRoute>
          }
        />

        {/*
          다른 앱에서 'TASTEE로 공유'해 들어오는 공용 입구.
          앱은 링크 종류를 가리지 않고 /share?url=... 로 보내고, 어디로 보낼지는
          SharedLink 가 정한다 — 받는 범위를 넓힐 때 앱을 다시 안 올려도 된다.
        */}
        <Route
          path="/share"
          element={
            <ProtectedRoute>
              <SharedLink />
            </ProtectedRoute>
          }
        />

        {/*
          기록할 사진 담아두기 — **개발 중에만 열린다** (2026-10-15).

          기능이 덜 됐다. 길이 열려 있으면 아직 못 쓰는 화면에 사용자가 닿는다.
          그렇다고 주석으로 막으면 실기기 테스트 때마다 풀었다 막았다 하게 되고,
          **한 번 깜빡하면 그대로 나간다.**

          COLLECT_READY 는 빌드할 때 상수로 박힌다 — 운영 빌드에서는 false 라
          이 블록이 통째로 사라진다(번들에 문자열조차 안 남는 걸 확인했다).
          완성되면 COLLECT_READY 를 true 로 바꾸면 끝이다.
        */}
        {/*
          대학교 연결 — 길은 열어두되, 홈의 들어가는 버튼은
          `canSeeUniversity` 를 통과한 계정에만 보인다(featureFlags.ts).
          화면 자체는 아직 자리만 잡아둔 상태다.
        */}
        {UNIVERSITY_READY && (
          <>
            <Route
              path="/university"
              element={
                <ProtectedRoute>
                  <University />
                </ProtectedRoute>
              }
            />
            <Route
              path="/university/email"
              element={
                <ProtectedRoute>
                  <UniversityEmail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/university/verify"
              element={
                <ProtectedRoute>
                  <UniversityVerify />
                </ProtectedRoute>
              }
            />
            <Route
              path="/university/done"
              element={
                <ProtectedRoute>
                  <UniversityDone />
                </ProtectedRoute>
              }
            />
            <Route
              path="/university/group"
              element={
                <ProtectedRoute>
                  <UniversityGroup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/university/rejoin"
              element={
                <ProtectedRoute>
                  <UniversityRejoin />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {/* 친구 초대(추천인 코드) — 다 될 때까지 개발 서버에서만(REFERRAL_READY) */}
        {REFERRAL_READY && (
          <Route
            path="/invite"
            element={
              <ProtectedRoute>
                <InviteFriends />
              </ProtectedRoute>
            }
          />
        )}

        {COLLECT_READY && (
          <>
            <Route
              path="/collect"
              element={
                <ProtectedRoute>
                  <Collect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/collect/guide"
              element={
                <ProtectedRoute>
                  <CollectGuide />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {/* 네이버지도·카카오맵 링크. /share 가 갈라 보낸다 */}
        <Route
          path="/share/map"
          element={
            <ProtectedRoute>
              <MapLinkShare />
            </ProtectedRoute>
          }
        />

        {/*
          인스타그램에서 공유해 들어오는 자리.

          /share 가 보내기도 하고, 예전 앱(1.0.42 이하)이 직접 이 주소로
          보내기도 한다. 그 버전들이 남아 있는 동안 이 자리를 지운다는 뜻이
          아니다 — 지우면 구버전 앱의 인스타 공유가 통째로 죽는다.
        */}
        <Route
          path="/share/instagram"
          element={
            <ProtectedRoute>
              <InstagramShare />
            </ProtectedRoute>
          }
        />

        {/* 저장한 장소 리스트 (카테고리별) */}
        <Route
          path="/user/:userId/saved-places/:category"
          element={
            <ProtectedRoute>
              <SavedPlacesList />
            </ProtectedRoute>
          }
        />

        {/* 플레이트 카테고리 */}
        <Route
          path="/user/:userId/plate-category/:categoryId"
          element={
            <ProtectedRoute>
              <PlateCategory />
            </ProtectedRoute>
          }
        />

        {/* 플레이트 카테고리 리스트 */}
        <Route
          path="/user/:userId/plate-category/:categoryId/:category"
          element={
            <ProtectedRoute>
              <PlateCategoryList />
            </ProtectedRoute>
          }
        />

        {/* 메인 - 마이 */}
        <Route
          path="/main-my"
          element={
            <ProtectedRoute>
              <MainMy />
            </ProtectedRoute>
          }
        />

        {/* 프로필 수정 */}
        <Route
          path="/profile-edit"
          element={
            <ProtectedRoute>
              <ProfileEdit />
            </ProtectedRoute>
          }
        />

        {/* 비밀번호 찾기 */}
        <Route path="/password-find" element={<PasswordFind />} />

        {/* 팔로워/팔로잉 */}
        <Route
          path="/follow-list/:type"
          element={
            <ProtectedRoute>
              <FollowList />
            </ProtectedRoute>
          }
        />

        {/* 팔로우 요청 */}
        <Route
          path="/follow-requests"
          element={
            <ProtectedRoute>
              <FollowRequests />
            </ProtectedRoute>
          }
        />

        {/* 리뷰 */}
        <Route
          path="/reviews"
          element={
            <ProtectedRoute>
              <Reviews />
            </ProtectedRoute>
          }
        />

        {/* 설정 */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* 계정 관리 */}
        <Route
          path="/account-management"
          element={
            <ProtectedRoute>
              <AccountManagement />
            </ProtectedRoute>
          }
        />

        {/* 차단한 사용자 — 차단을 풀 수 있는 유일한 자리 */}
        <Route
          path="/blocked-users"
          element={
            <ProtectedRoute>
              <BlockedUsers />
            </ProtectedRoute>
          }
        />

        {/* 공지사항 */}
        <Route
          path="/notice"
          element={
            <ProtectedRoute>
              <NoticePage />
            </ProtectedRoute>
          }
        />

        {/* 옛 이용약관 URL — 외부(스토어·카카오/네이버 콘솔 등) 등록분 404 방지용 리다이렉트 */}
        <Route
          path="/terms-of-service"
          element={<Navigate to="/terms" replace />}
        />

        {/* 동의 관리 */}
        <Route
          path="/consent-management"
          element={
            <ProtectedRoute>
              <ConsentManagement />
            </ProtectedRoute>
          }
        />

        {/* 법적 문서 (공개 — 로그인 불필요, 앱 셸 없이 문서만: 웹뷰 [보기]·스토어 공개 URL) */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/location-terms" element={<LocationTermsPage />} />
        <Route path="/marketing-consent" element={<MarketingConsentPage />} />

        <Route path="/account-deletion" element={<AccountDeletion />} />

        {/* 본인인증 테스트 */}
        <Route
          path="/phone-verification-test"
          element={<PhoneVerificationTest />}
        />

        {/* 관리자 */}
        <Route path="/admin" element={<AdminRedirect />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/stats" element={<AdminStats />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/places" element={<AdminPlaces />} />
        <Route
          path="/admin/place-categories"
          element={<AdminPlaceCategories />}
        />
        <Route path="/admin/reviews" element={<AdminReviews />} />
        <Route path="/admin/curated-feed" element={<AdminCuratedFeed />} />
        <Route path="/admin/reports" element={<AdminReports />} />
        <Route path="/admin/inquiries" element={<AdminInquiries />} />
        <Route path="/admin/notices" element={<AdminNotices />} />
        <Route path="/admin/push" element={<AdminPush />} />
        <Route
          path="/admin/review-keywords"
          element={<AdminReviewKeywords />}
        />
        <Route
          path="/admin/default-review-categories"
          element={<AdminDefaultReviewCategories />}
        />
        <Route
          path="/admin/review-keyword-suggestions"
          element={<AdminReviewKeywordSuggestions />}
        />
        <Route
          path="/admin/place-suggestions"
          element={<AdminPlaceSuggestions />}
        />
        <Route path="/admin/map-settings" element={<AdminMapSettings />} />
        <Route path="/admin/policies" element={<AdminPolicies />} />
        <Route path="/admin/app-versions" element={<AdminAppVersions />} />
        <Route path="/admin/place-names" element={<AdminPlaceNames />} />
        <Route
          path="/admin/instagram-test"
          element={<AdminInstagramTest />}
        />
        <Route path="/admin/toast-preview" element={<AdminToastPreview />} />
        <Route
          path="/admin/terms-of-service"
          element={<AdminTermsOfService />}
        />
        <Route path="/admin/privacy-policy" element={<AdminPrivacyPolicy />} />
      </Routes>

      {/* 코치마크 튜토리얼: 라우트 밖에서 전역 렌더 → 홈→마이 탭 등 페이지가 바뀌어도 유지 */}
      {tutorialActive && (
        <CoachMark
          steps={HOME_TUTORIAL_STEPS}
          onClose={() => {
            markHomeTutorialSeen();
            closeTutorial();
          }}
        />
      )}
    </div>
  );
};

export default AppRoutes;
